import * as Sentry from '@sentry/react';

import { openNotification } from '@/components/UI/DesktopNotifications';

import { IS_SERVER } from './ssr';
import { type SoundFailureReason, reportSound } from './stat/sound';
import type { ToneName } from './toneGate';

export interface CustomEventMap {
  rasplayerror: CustomEvent;
}

/**
 * Every sound the app makes. The five Release Center tones plus `pop`, the DM
 * chime — one helper owns them all so preloading, the autoplay unlock, the retry
 * and the telemetry apply uniformly (#2041).
 */
export type SoundName = ToneName | 'pop';

/**
 * Each sound is bundled with the app first and fetched from the CDN only as a
 * fallback.
 *
 * Bundling matters twice over. On the web the local copy is same-origin and
 * served by the Worker (see the `/sounds/` branch in worker/index.ts), so the
 * first GO does not wait on a cross-origin CDN round-trip at the exact moment it
 * has to be audible. In the Capacitor shell `public/` is copied into
 * `dist/client` and then into the app bundle, so the file resolves off disk with
 * no network at all. The CDN URL stays as a second source purely so an app build
 * that somehow shipped without the asset still makes a sound.
 */
const SOURCES: Record<SoundName, readonly string[]> = {
  go: ['/sounds/go-25.mp3', 'https://cdn.raspicks.com/go-25.mp3'],
  cancel: ['/sounds/cancel-25.mp3', 'https://cdn.raspicks.com/cancel-25.mp3'],
  opposite: ['/sounds/opposite-25.mp3', 'https://cdn.raspicks.com/opposite-25.mp3'],
  setup: ['/sounds/setup-25.mp3', 'https://cdn.raspicks.com/setup-25.mp3'],
  message: ['/sounds/msg.mp3', 'https://cdn.raspicks.com/msg.mp3'],
  pop: ['/sounds/pop.mp3', 'https://cdn.raspicks.com/pop.mp3'],
};

export const SOUND_NAMES = Object.keys(SOURCES) as SoundName[];

/** Human label for the Settings "Test sounds" control. */
export const SOUND_LABELS: Record<SoundName, string> = {
  setup: 'Set up',
  go: 'GO',
  cancel: 'CANCEL',
  opposite: 'OPPOSITE',
  message: 'Message',
  pop: 'Support chat',
};

type AudioContextCtor = new () => AudioContext;

function audioContextCtor(): AudioContextCtor | undefined {
  if (IS_SERVER || typeof window === 'undefined') {
    return undefined;
  }
  return (
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext
  );
}

let ctx: AudioContext | null = null;

/**
 * The shared AudioContext, created lazily. Safari/iOS start it `suspended` until
 * a user gesture resumes it (that is what `unlockAudio` is for), but decoding
 * into it works while suspended, so creating it early is what lets the tones be
 * decode-complete before the first GO.
 */
function getCtx(): AudioContext | null {
  if (ctx) {
    return ctx;
  }
  const Ctor = audioContextCtor();
  if (!Ctor) {
    return null;
  }
  try {
    ctx = new Ctor();
  } catch {
    ctx = null;
  }
  return ctx;
}

const buffers = new Map<SoundName, AudioBuffer>();
const decoding = new Map<SoundName, Promise<AudioBuffer | null>>();
const elements = new Map<SoundName, HTMLAudioElement>();

let unlocked = false;
let notifiedFailure = false;

/** Has a user gesture unlocked audio output in this document yet? */
export function audioUnlocked(): boolean {
  return unlocked;
}

function classifyError(error: unknown): SoundFailureReason {
  const name = (error as { name?: string } | null)?.name;
  const message = `${(error as { message?: string } | null)?.message || ''}`;

  if (name === 'NotAllowedError') {
    return 'autoplay-blocked';
  }
  if (name === 'EncodingError' || /decod/i.test(message)) {
    return 'decode';
  }
  if (
    name === 'NotSupportedError' ||
    name === 'AbortError' ||
    /fetch|network|load/i.test(message)
  ) {
    return 'network';
  }
  return 'unknown';
}

/** Fetch + decode one sound into an AudioBuffer, trying each source in turn. */
async function decode(name: SoundName): Promise<AudioBuffer | null> {
  const context = getCtx();
  if (!context) {
    return null;
  }

  let lastError: unknown;

  for (const url of SOURCES[name]) {
    try {
      const res = await fetch(url, { cache: 'force-cache' });
      if (!res.ok) {
        lastError = new Error(`sound fetch ${res.status}`);
        continue;
      }
      const bytes = await res.arrayBuffer();
      const buffer = await context.decodeAudioData(bytes);
      buffers.set(name, buffer);
      return buffer;
    } catch (error) {
      lastError = error;
    }
  }

  reportSound('failed', { name, reason: classifyError(lastError), stage: 'decode' });
  return null;
}

function ensureDecoded(name: SoundName): Promise<AudioBuffer | null> {
  const ready = buffers.get(name);
  if (ready) {
    return Promise.resolve(ready);
  }
  let pending = decoding.get(name);
  if (!pending) {
    pending = decode(name).finally(() => {
      decoding.delete(name);
    });
    decoding.set(name, pending);
  }
  return pending;
}

/** The `<audio>` fallback element for one sound, primed to load eagerly. */
function ensureElement(name: SoundName): HTMLAudioElement | null {
  if (IS_SERVER || typeof Audio === 'undefined') {
    return null;
  }
  let el = elements.get(name);
  if (!el) {
    el = new Audio(SOURCES[name][0]);
    el.preload = 'auto';
    // A failed local source falls back to the CDN copy on the next play.
    el.addEventListener('error', () => {
      const cdn = SOURCES[name][1];
      if (cdn && el && el.src !== cdn) {
        el.src = cdn;
      }
    });
    elements.set(name, el);
  }
  return el;
}

/**
 * Warm every sound so the first one to actually matter is already in memory.
 * Safe to call more than once; decodes are de-duplicated.
 */
export function preloadSounds(): void {
  if (IS_SERVER) {
    return;
  }
  for (const name of SOUND_NAMES) {
    if (getCtx()) {
      void ensureDecoded(name);
    } else {
      ensureElement(name)?.load();
    }
  }
}

/**
 * Satisfy the browser's autoplay policy from inside a user gesture.
 *
 * Both engines need it: an AudioContext created before any interaction is
 * `suspended` and has to be resumed, and the `<audio>` fallback needs one
 * successful `play()` inside a gesture before later programmatic plays are
 * allowed. Pushing a zero-gain silent buffer through is inaudible but counts.
 *
 * Must be called synchronously from a real gesture handler — `await`ing
 * something first spends the gesture and Safari refuses.
 */
export async function unlockAudio(): Promise<boolean> {
  if (IS_SERVER) {
    return false;
  }

  let ok = false;
  const context = getCtx();

  if (context) {
    try {
      if (context.state === 'suspended') {
        await context.resume();
      }
      const source = context.createBufferSource();
      source.buffer = context.createBuffer(1, 1, context.sampleRate);
      const gain = context.createGain();
      gain.gain.value = 0;
      source.connect(gain);
      gain.connect(context.destination);
      source.start(0);
      ok = context.state === 'running';
    } catch {
      ok = false;
    }
  }

  if (!ok) {
    // No Web Audio, or it refused — unlock the element path instead.
    const el = ensureElement('pop');
    if (el) {
      const wasMuted = el.muted;
      try {
        el.muted = true;
        await el.play();
        el.pause();
        el.currentTime = 0;
        ok = true;
      } catch {
        ok = false;
      } finally {
        el.muted = wasMuted;
      }
    }
  }

  if (ok) {
    unlocked = true;
    // The gesture is also the first moment decoding is certain to be allowed.
    preloadSounds();
  }

  return ok;
}

const UNLOCK_EVENTS = ['pointerdown', 'keydown', 'touchend'] as const;

/**
 * Unlock audio on this document's first user gesture, once.
 *
 * Without this the very first tone of a session is the one that fails: the user
 * has loaded the page but not clicked anything, so `play()` rejects with
 * NotAllowedError and the GO is silent. Any interaction at all — a click, a key,
 * a tap anywhere — is enough.
 */
export function installAudioUnlock(): () => void {
  const noop = () => {
    // Nothing installed, so nothing to remove.
  };

  if (IS_SERVER || unlocked) {
    return noop;
  }

  const onGesture = () => {
    void unlockAudio().then((ok) => {
      if (ok) {
        remove();
      }
    });
  };

  const remove = () => {
    for (const type of UNLOCK_EVENTS) {
      window.removeEventListener(type, onGesture);
    }
  };

  for (const type of UNLOCK_EVENTS) {
    window.addEventListener(type, onGesture, { passive: true });
  }

  return remove;
}

async function playBuffer(name: SoundName): Promise<void> {
  const context = getCtx();
  if (!context) {
    throw new Error('no audio context');
  }
  const buffer = await ensureDecoded(name);
  if (!buffer) {
    throw new Error('sound decode failed');
  }
  if (context.state === 'suspended') {
    // Only succeeds if a gesture has already happened.
    await context.resume();
  }
  if (context.state !== 'running') {
    // `source.start()` on a suspended context does NOT throw — it silently
    // queues. Without this check a blocked tone would be reported as played,
    // which is precisely the blind spot this work exists to remove. Throw a
    // NotAllowedError so it classifies as autoplay-blocked and falls through to
    // the element path, which does reject when it is blocked.
    throw new DOMException('audio context is not running', 'NotAllowedError');
  }
  const source = context.createBufferSource();
  source.buffer = buffer;
  source.connect(context.destination);
  source.start(0);
}

async function playElement(name: SoundName): Promise<void> {
  const el = ensureElement(name);
  if (!el) {
    throw new Error('no audio element');
  }
  try {
    el.currentTime = 0;
  } catch {
    // Not seekable yet; play from wherever it is.
  }
  await el.play();
}

function onPlayFailure(reason: SoundFailureReason) {
  try {
    window.dispatchEvent(new CustomEvent('rasplayerror'));
  } catch (error) {
    Sentry.captureException(error);
  }

  try {
    if (!notifiedFailure) {
      notifiedFailure = true;
      // This used to be suppressed on Apple devices ("iOS can't play audio
      // anyways"). With the gesture unlock in place iOS *can*, so a failure
      // there is a real failure worth telling the user about — silently
      // swallowing it is how "I never heard the GO" went unnoticed.
      //
      // An autoplay block is the one failure the user can fix themselves, so it
      // gets its own wording and points at the Settings control that performs
      // the unlock.
      openNotification(
        'RAS App',
        reason === 'autoplay-blocked'
          ? 'RAS needs a tap before it can play notification sounds. Open the app, or use Test sounds in Settings.'
          : 'RAS was unable to play notification sounds. Please see the app.',
        reason === 'autoplay-blocked' ? '/settings?scrollTo=sounds' : undefined,
        'audioerr',
      );
    }
  } catch {
    //
  }
}

export interface PlayToneMeta {
  /** Why this sound is playing — carried into telemetry. */
  late?: boolean;
  ageMs?: number;
}

/**
 * Play one sound: decoded Web Audio buffer first, `<audio>` element as the
 * fallback, one retry, then a reported failure with a reason.
 *
 * Resolves true when something was actually handed to the audio engine.
 */
export async function playSound(name: SoundName, meta?: PlayToneMeta): Promise<boolean> {
  if (IS_SERVER) {
    return false;
  }

  const attempts: Array<() => Promise<void>> = [
    () => playBuffer(name),
    () => playElement(name),
    // The retry: a decode that lost a race or an element that had not loaded
    // yet usually succeeds the second time round.
    () => playElement(name),
  ];

  let lastError: unknown;

  for (const attempt of attempts) {
    try {
      await attempt();
      reportSound('played', { name, late: meta?.late, ageMs: meta?.ageMs });
      return true;
    } catch (error) {
      lastError = error;
    }
  }

  const reason = classifyError(lastError);
  reportSound('failed', {
    name,
    reason,
    stage: 'play',
    late: meta?.late,
    ageMs: meta?.ageMs,
    unlocked,
  });
  onPlayFailure(reason);

  return false;
}

export default playSound;
